/**
 * Click-to-select and property-set extraction.
 *
 * Emits plain data; it never touches the DOM. The properties panel subscribes.
 * This is what keeps `viewer/` free of `ui/` imports (CODE_RULES.md §2).
 *
 * API verified against @thatopen/components-front 3.4.4 type definitions.
 */
import * as THREE from "three";
import type * as OBC from "@thatopen/components";
import * as OBF from "@thatopen/components-front";
import type * as FRAGS from "@thatopen/fragments";
import { HIGHLIGHT } from "../core/config";
import { Emitter } from "../core/emitter";
import { logger } from "../core/logger";
import type { ArchWorld } from "./world";

const SELECT_EVENT = "select";

export interface PropertyRow {
  readonly name: string;
  readonly value: string;
}

export interface PropertySet {
  readonly name: string;
  readonly rows: readonly PropertyRow[];
}

export interface SelectedElement {
  readonly name: string;
  readonly category: string;
  readonly sets: readonly PropertySet[];
}

export interface SelectionEvents extends Record<string, unknown> {
  selected: SelectedElement;
  cleared: undefined;
}

export function createSelection(
  components: OBC.Components,
  world: ArchWorld,
  fragments: OBC.FragmentsManager,
): Emitter<SelectionEvents> {
  const events = new Emitter<SelectionEvents>();
  const highlighter = components.get(OBF.Highlighter);

  highlighter.setup({
    world,
    selectName: SELECT_EVENT,
    selectMaterialDefinition: {
      color: new THREE.Color(HIGHLIGHT.SELECT_COLOR),
      opacity: 1,
      transparent: false,
      renderedFaces: 0,
    },
  });

  const selectEvents = highlighter.events[SELECT_EVENT];
  if (selectEvents === undefined) {
    logger.error("Highlighter select events missing; selection disabled.");
    return events;
  }

  selectEvents.onHighlight.add((modelIdMap: OBC.ModelIdMap) => {
    void describeFirst(fragments, modelIdMap).then((element) => {
      if (element !== null) events.emit("selected", element);
    });
  });

  selectEvents.onClear.add(() => {
    events.emit("cleared", undefined);
  });

  return events;
}

async function describeFirst(
  fragments: OBC.FragmentsManager,
  modelIdMap: OBC.ModelIdMap,
): Promise<SelectedElement | null> {
  for (const [modelId, localIds] of Object.entries(modelIdMap)) {
    const model = fragments.list.get(modelId);
    const [localId] = [...localIds];
    if (model === undefined || localId === undefined) continue;

    try {
      const [data] = await model.getItemsData([localId], {
        attributesDefault: true,
        relations: {
          IsDefinedBy: { attributes: true, relations: true },
          DefinesOccurrence: { attributes: false, relations: false },
        },
      });
      if (data === undefined) continue;
      return {
        name: readValue(data["Name"]) ?? "Unnamed element",
        category: readValue(data["_category"]) ?? "Unknown",
        sets: readPropertySets(data),
      };
    } catch (cause) {
      logger.error("Could not read element properties", cause);
      return null;
    }
  }
  return null;
}

function readPropertySets(data: FRAGS.ItemData): PropertySet[] {
  const raw: unknown = data["IsDefinedBy"];
  if (!Array.isArray(raw)) return [];

  const sets: PropertySet[] = [];
  for (const entry of raw as readonly unknown[]) {
    const set = readSingleSet(entry);
    if (set !== null) sets.push(set);
  }
  return sets;
}

function readSingleSet(entry: unknown): PropertySet | null {
  const record = asRecord(entry);
  if (record === null) return null;

  const properties: unknown = record["HasProperties"];
  if (!Array.isArray(properties)) return null;

  const rows = readRows(properties as readonly unknown[]);
  if (rows.length === 0) return null;

  return { name: readValue(record["Name"]) ?? "Properties", rows };
}

function readRows(properties: readonly unknown[]): PropertyRow[] {
  const rows: PropertyRow[] = [];
  for (const property of properties) {
    const record = asRecord(property);
    if (record === null) continue;
    const name = readValue(record["Name"]);
    const value = readValue(record["NominalValue"]);
    if (name !== null && value !== null) rows.push({ name, value });
  }
  return rows;
}

/** The single narrowing point for untyped IFC payload objects. */
function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "object" || value === null) return null;
  return value as Record<string, unknown>;
}

/** Fragments wraps scalars as `{ value: ... }`; unwrap to a display string. */
function readValue(raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "string") return raw;
  if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
  if (typeof raw === "object" && "value" in raw) return readValue(raw.value);
  return null;
}
